import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { doc, getDoc, getDocFromServer } from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { usePessimisticLock } from './usePessimisticLock'
import { subscribeToAppLifecycle } from '../utils/appLifecycle'
import type { DocumentLock } from '../types/schema'

export const IDLE_RELEASE_MS = 20_000

export interface UseEditLockOptions {
  workspaceId: string | null
  actor: { uid: string; displayName: string } | null
  isAdmin?: boolean
  enabled?: boolean
  onLockConflict?: (holder: string) => void
}

export interface EditLockState {
  holder: string | null
  heldByMe: boolean
  isCheckingLock: boolean
  hasAcquiredLock: boolean
  acquireOnEdit: () => Promise<boolean>
  release: (opts?: { override?: boolean }) => Promise<void>
  recheck: () => Promise<void>
  markActivity: () => void
  armIdleRelease: (enabled: boolean) => void
  overrideLock: () => Promise<boolean>
}

const getExpiryMs = (lockData: Partial<DocumentLock>): number => {
  const expiresAt = lockData.expiresAt
  if (expiresAt && typeof expiresAt === 'object' && 'toMillis' in expiresAt && typeof (expiresAt as { toMillis: () => number }).toMillis === 'function') {
    return (expiresAt as { toMillis: () => number }).toMillis()
  }
  if (expiresAt instanceof Date) return expiresAt.getTime()
  if (typeof expiresAt === 'number') return expiresAt
  return 0
}

export function useEditLock(
  itemId: string | null,
  options: UseEditLockOptions,
): EditLockState {
  const { workspaceId, actor, isAdmin = false, enabled = true, onLockConflict } = options
  const { locks, acquireEditLease, releaseLease, getExternalLock } = usePessimisticLock()

  const [localHeld, setLocalHeld] = useState(false)
  const [hasAcquiredLock, setHasAcquiredLock] = useState(false)
  const [isCheckingLock, setIsCheckingLock] = useState(false)
  const [recheckOverride, setRecheckOverride] = useState<{
    forExternalLock: DocumentLock | null
    holder: string | null
  } | null>(null)

  // Live peer lock from usePessimisticLock subscription
  const externalLock = useMemo(() => (itemId ? getExternalLock(itemId) : null), [getExternalLock, itemId])
  const peerLockHolder = externalLock?.lockedByName ?? null

  // Determine if held by current actor in activeLocks
  const isHeldByMeInLocks = useMemo(() => {
    if (!itemId || !actor?.uid) return false
    const current = locks.find((lock) => lock.id === itemId)
    return Boolean(current && current.lockedBy === actor.uid)
  }, [actor?.uid, itemId, locks])

  // Peer lock takes precedence over optimistic localHeld
  const heldByMe = Boolean(!peerLockHolder && (localHeld || isHeldByMeInLocks))

  const inFlightAcquireRef = useRef<Promise<boolean> | null>(null)
  const onLockConflictRef = useRef(onLockConflict)
  const itemIdRef = useRef(itemId)
  const actorRef = useRef(actor)
  const workspaceIdRef = useRef(workspaceId)
  const isAdminRef = useRef(isAdmin)
  const heldByMeRef = useRef(heldByMe)
  const externalLockRef = useRef<DocumentLock | null>(externalLock)

  // --- Idle timer state ---
  const idleReleaseArmedRef = useRef(false)
  const idleTimerRef = useRef<number | null>(null)
  const lastActivityAtRef = useRef(0)

  useEffect(() => {
    onLockConflictRef.current = onLockConflict
    itemIdRef.current = itemId
    actorRef.current = actor
    workspaceIdRef.current = workspaceId
    isAdminRef.current = isAdmin
    heldByMeRef.current = heldByMe
    externalLockRef.current = externalLock
  })

  const holder = heldByMe
    ? null
    : recheckOverride && recheckOverride.forExternalLock === externalLock
      ? recheckOverride.holder
      : peerLockHolder

  const release = useCallback(
    async (opts?: { override?: boolean }) => {
      if (!enabled) return
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
      setLocalHeld(false)
      heldByMeRef.current = false
      const currentItemId = itemIdRef.current
      if (!currentItemId) return
      if (!heldByMeRef.current && !opts?.override) {
        // no-op if not held unless override specified
      }
      try {
        await releaseLease(currentItemId)
      } catch (error) {
        console.warn('[useEditLock] releaseLease failed', error)
      }
    },
    [enabled, releaseLease],
  )

  const armIdleRelease = useCallback(
    (armEnabled: boolean) => {
      if (!enabled) return
      idleReleaseArmedRef.current = armEnabled
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
      if (armEnabled) {
        idleTimerRef.current = window.setTimeout(() => {
          if (heldByMeRef.current) {
            void release()
          }
        }, IDLE_RELEASE_MS)
      }
    },
    [enabled, release],
  )

  const markActivity = useCallback(() => {
    if (!enabled) return
    lastActivityAtRef.current = Date.now()
    if (idleReleaseArmedRef.current) {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
      idleTimerRef.current = window.setTimeout(() => {
        if (heldByMeRef.current) {
          void release()
        }
      }, IDLE_RELEASE_MS)
    }
  }, [enabled, release])

  const acquireOnEdit = useCallback(async (): Promise<boolean> => {
    if (!enabled) return true
    if (heldByMeRef.current) return true

    const currentWorkspaceId = workspaceIdRef.current
    const currentActor = actorRef.current
    const currentItemId = itemIdRef.current

    if (!currentWorkspaceId || !currentActor) {
      if (import.meta.env.DEV) {
        throw new Error('[useEditLock] acquireOnEdit requires workspaceId and actor.')
      }
      return false
    }

    if (!currentItemId) return false

    if (inFlightAcquireRef.current) {
      return inFlightAcquireRef.current
    }

    const acquirePromise = (async () => {
      try {
        await acquireEditLease(currentItemId)
        setLocalHeld(true)
        heldByMeRef.current = true
        setHasAcquiredLock(true)
        setRecheckOverride(null)
        return true
      } catch {
        const conflictHolder = getExternalLock(currentItemId)?.lockedByName || 'A teammate'
        onLockConflictRef.current?.(conflictHolder)
        return false
      } finally {
        inFlightAcquireRef.current = null
      }
    })()

    inFlightAcquireRef.current = acquirePromise
    return acquirePromise
  }, [acquireEditLease, enabled, getExternalLock])

  const recheck = useCallback(async () => {
    if (!enabled) return
    const currentWorkspaceId = workspaceIdRef.current
    const currentItemId = itemIdRef.current
    const currentActor = actorRef.current

    if (!currentWorkspaceId || !currentItemId) return

    setIsCheckingLock(true)
    try {
      const lockDocRef = doc(firestoreDb, 'workspaces', currentWorkspaceId, 'locks', currentItemId)
      let snap
      try {
        snap = await getDocFromServer(lockDocRef)
      } catch {
        snap = await getDoc(lockDocRef)
      }

      if (!snap.exists()) {
        setRecheckOverride({ forExternalLock: externalLockRef.current, holder: null })
      } else {
        const data = snap.data() as Partial<DocumentLock>
        const now = Date.now()
        const expiry = getExpiryMs(data)
        if (expiry <= now) {
          setRecheckOverride({ forExternalLock: externalLockRef.current, holder: null })
        } else if (data.lockedBy === currentActor?.uid) {
          setRecheckOverride({ forExternalLock: externalLockRef.current, holder: null })
          setLocalHeld(true)
          heldByMeRef.current = true
        } else {
          setRecheckOverride({ forExternalLock: externalLockRef.current, holder: data.lockedByName || 'A teammate' })
          setLocalHeld(false)
          heldByMeRef.current = false
        }
      }
    } catch (error) {
      console.warn('[useEditLock] recheck failed', error)
    } finally {
      setIsCheckingLock(false)
    }
  }, [enabled])

  const overrideLock = useCallback(async (): Promise<boolean> => {
    if (!enabled) return true
    if (!isAdminRef.current) return false

    const currentWorkspaceId = workspaceIdRef.current
    const currentActor = actorRef.current
    const currentItemId = itemIdRef.current

    if (!currentWorkspaceId || !currentActor || !currentItemId) return false

    try {
      await acquireEditLease(currentItemId, { override: true })
      setLocalHeld(true)
      heldByMeRef.current = true
      setHasAcquiredLock(true)
      setRecheckOverride(null)
      return true
    } catch {
      return false
    }
  }, [acquireEditLease, enabled])

  // 30 s lease renewal interval when heldByMe
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (heldByMeRef.current && itemIdRef.current) {
        void acquireEditLease(itemIdRef.current).catch((err) => {
          console.warn('[useEditLock] lease renewal failed', err)
        })
      }
    }, 30_000)
    return () => window.clearInterval(intervalId)
  }, [acquireEditLease])

  // App lifecycle listener (Capacitor background/pause or web pagehide/hidden)
  useEffect(() => {
    const sub = subscribeToAppLifecycle((event) => {
      if (event === 'backgrounded' || event === 'pause') {
        if (heldByMeRef.current && itemIdRef.current) {
          void release()
        }
      }
    })
    return () => sub.remove()
  }, [release])

  // Web beforeunload listener as belt-and-suspenders
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = () => {
      if (heldByMeRef.current && itemIdRef.current) {
        void releaseLease(itemIdRef.current)
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [releaseLease])

  // Unmount effect: if heldByMe, fire-and-forget release
  useEffect(() => {
    return () => {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
      if (heldByMeRef.current && itemIdRef.current) {
        void releaseLease(itemIdRef.current)
      }
    }
  }, [releaseLease])

  return useMemo(
    () => ({
      holder,
      heldByMe,
      isCheckingLock,
      hasAcquiredLock,
      acquireOnEdit,
      release,
      recheck,
      markActivity,
      armIdleRelease,
      overrideLock,
    }),
    [
      acquireOnEdit,
      armIdleRelease,
      hasAcquiredLock,
      heldByMe,
      holder,
      isCheckingLock,
      markActivity,
      overrideLock,
      recheck,
      release,
    ],
  )
}

export const createWithLock = (
  editLock: Pick<EditLockState, 'acquireOnEdit' | 'markActivity'>,
) => {
  return <T extends (...args: unknown[]) => unknown>(fn: T): T => {
    return (async (...args: Parameters<T>) => {
      const ok = await editLock.acquireOnEdit()
      if (!ok) return undefined
      const result = await fn(...args)
      editLock.markActivity()
      return result
    }) as unknown as T
  }
}
